import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, forkJoin } from 'rxjs';
import DefaultProjectEmployeeLinkDTO from 'src/app/models/DefaultProjectEmployeeLinkDTO';
import { Employee } from 'src/app/models/Employee';
import { EmployeeContract } from 'src/app/models/EmployeeContract';
import Project from 'src/app/models/Project';

import { ProjectService } from '../../projects/project.service';
import { EmployeeService } from '../employees.service';

type ContractScope = 'CLIENT' | 'EMPLOYEE';

@Component({
    selector: 'app-add-employee-contract-modal',
    templateUrl: './add-employee-contract-modal.component.html',
    styleUrls: ['./add-employee-contract-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class AddEmployeeContractModalComponent implements OnInit, OnDestroy {
    @Input() employee: Employee;
    @Input() scope: ContractScope = 'CLIENT';

    private subscriptions = new Subscription();

    form: UntypedFormGroup;
    projects: Project[] = [];
    selectedProjectIds: string[] = [];
    filterText = '';
    loading = false;

    constructor(
        public activeModal: NgbActiveModal,
        private fb: UntypedFormBuilder,
        private employeeService: EmployeeService,
        private projectService: ProjectService,
        private logger: NGXLogger,
        private toastr: ToastrService,
    ) {}

    ngOnInit(): void {
        this.form = this.fb.group({
            pricePerHour: ['', Validators.required],
            effectiveFrom: ['', Validators.required],
            effectiveTo: [''],
            contractFile: [null],
            poNumber: [''],
            sesNumber: [''],
            contractAgreementNumber: [''],
            freeText: [''],
        });

        if (this.scope === 'CLIENT') {
            this.subscriptions.add(
                this.employeeService.getAllProjects().subscribe((projects) => {
                    this.projects = (projects || []).sort((a, b) => (a.title || '').localeCompare(b.title || ''));
                }),
            );
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get filteredProjects(): Project[] {
        const query = (this.filterText || '').trim().toLowerCase();
        if (!query) return this.projects;
        return this.projects.filter((project) => (project.title || '').toLowerCase().includes(query));
    }

    isProjectSelected(project: Project): boolean {
        return !!project?.uuid && this.selectedProjectIds.includes(project.uuid);
    }

    toggleProject(project: Project): void {
        if (!project?.uuid) return;
        if (this.isProjectSelected(project)) {
            this.selectedProjectIds = this.selectedProjectIds.filter((id) => id !== project.uuid);
        } else {
            this.selectedProjectIds.push(project.uuid);
        }
    }

    private readFileAsBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = (reader.result as string) || '';
                const base64 = result.includes('base64,') ? result.split('base64,')[1] : result;
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    async save(): Promise<void> {
        if (!this.employee?.uuid) {
            this.toastr.error('Employee not found.');
            return;
        }

        if (this.form.invalid || (this.scope === 'CLIENT' && this.selectedProjectIds.length === 0)) {
            this.form.markAllAsTouched();
            if (this.scope === 'CLIENT' && this.selectedProjectIds.length === 0) {
                this.toastr.info('Select at least one project.');
            }
            return;
        }

        this.loading = true;

        try {
            const payloadBase = {
                pricePerHour: this.form.value.pricePerHour,
                effectiveFrom: this.form.value.effectiveFrom,
                effectiveTo: this.form.value.effectiveTo,
                poNumber: this.form.value.poNumber,
                sesNumber: this.form.value.sesNumber,
                contractAgreementNumber: this.form.value.contractAgreementNumber,
                freeText: this.form.value.freeText,
            };

            let contractUrl: string = null;
            let contractBase64: string = null;
            let contractFileName: string = null;
            let contractMimeType: string = null;

            const contractFileValue: any = this.form.value?.contractFile || null;
            if (contractFileValue) {
                if (typeof contractFileValue === 'string') {
                    contractUrl = contractFileValue;
                    try {
                        const parts = contractFileValue.split('/');
                        contractFileName = parts[parts.length - 1];
                    } catch (e) {}
                } else if (contractFileValue instanceof File) {
                    const base64 = await this.readFileAsBase64(contractFileValue);
                    contractBase64 = base64;
                    contractFileName = contractFileValue.name;
                    contractMimeType = contractFileValue.type || 'application/octet-stream';
                }
            }

            if (this.scope === 'CLIENT') {
                const requests: Observable<any>[] = this.selectedProjectIds.map((projectId) => {
                    const dto: DefaultProjectEmployeeLinkDTO = {
                        projectId,
                        employeeIds: [this.employee.uuid],
                        pricePerHour: payloadBase.pricePerHour,
                        effectiveFrom: payloadBase.effectiveFrom,
                        effectiveTo: payloadBase.effectiveTo,
                        poNumber: payloadBase.poNumber,
                        sesNumber: payloadBase.sesNumber,
                        contractAgreementNumber: payloadBase.contractAgreementNumber,
                        freeText: payloadBase.freeText,
                        contractUrl,
                        contractBase64,
                        contractFileName,
                        contractMimeType,
                    };
                    return this.projectService.addDefaultEmployees(dto);
                });

                this.subscriptions.add(
                    forkJoin(requests).subscribe({
                        next: () => {
                            this.toastr.success('Contract(s) added successfully.');
                            this.activeModal.close(true);
                        },
                        error: () => {
                            this.logger.error('Failed to add client contracts');
                            this.toastr.error('Failed to add contract(s).');
                            this.loading = false;
                        },
                    }),
                );
                return;
            }

            const employeeContractPayload: EmployeeContract = {
                pricePerHour: payloadBase.pricePerHour,
                effectiveFrom: payloadBase.effectiveFrom,
                effectiveTo: payloadBase.effectiveTo,
                poNumber: payloadBase.poNumber,
                sesNumber: payloadBase.sesNumber,
                contractAgreementNumber: payloadBase.contractAgreementNumber,
                freeText: payloadBase.freeText,
                contractUrl,
                contractBase64,
                contractFileName,
                contractMimeType,
            };

            this.subscriptions.add(
                this.employeeService.addEmployeeContract(this.employee.uuid, employeeContractPayload).subscribe({
                    next: () => {
                        this.toastr.success('Contract added successfully.');
                        this.activeModal.close(true);
                    },
                    error: () => {
                        this.logger.error('Failed to add employee contract');
                        this.toastr.error('Failed to add contract.');
                        this.loading = false;
                    },
                }),
            );
        } catch (error) {
            this.logger.error('Failed preparing employee contract payload', error);
            this.toastr.error('Failed to process contract file.');
            this.loading = false;
        }
    }
}
